import { useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TravelActivityType, travelFundingSourceList } from '@/module/Travel/Request/model/TravelRequestTypes';

import { FormControlInput } from '@/core/components';
import { CustomSelect } from '@/core/components/Select';

export interface TravelFundingSourceSelectControlProps<T extends FieldValues> extends FormControlInput<T> {
  label: string;
  isDisabled?: boolean;

  activityType: TravelActivityType;
  repeated?: boolean;
}

export function TravelFundingSourceSelectControl<T extends FieldValues>(
  props: TravelFundingSourceSelectControlProps<T>,
) {
  const { label, name, control, isSubmitting, isDisabled, activityType, repeated } = props;
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const options = useMemo(
    () =>
      travelFundingSourceList
        .filter(value => activityType !== 'ERASMUS' || value === 'ERASMUS_FUNDS')
        .map(value => ({
          value,
          label: t(`travel.fundingSourceEnum.${value}`),
          // Listed but greyed out, so a requester learns that Erasmus funds belong to Erasmus requests only.
          disabled: activityType !== 'ERASMUS' && value === 'ERASMUS_FUNDS',
        })),
    [activityType, t],
  );
  // The schema's only custom issue on this field is the repeat, which goes stale on the rows an edit does not revalidate.
  const ownError = error?.type === 'custom' ? undefined : error?.message;
  const repeatError = repeated ? t('custom.expenseSourceRepeated', { ns: 'formValidation' }) : undefined;

  return (
    <CustomSelect
      searchable
      data={options}
      ref={field.ref}
      name={field.name}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      label={label}
      placeholder={label}
      withAsterisk
      error={[ownError, repeatError].filter(Boolean).join('; ') || undefined}
      disabled={isSubmitting || isDisabled}
      flex={1}
    />
  );
}
